/**
 * Web-only state for login glass → chrome shell morph.
 * Lives outside React so Auth unmount does not clear the captured rect.
 *
 * Fade is driven by Dashboard mount (`notifyDashboardShellReady`), not a fixed timer,
 * so the overlay covers lazy DashStack load.
 */

import {
  GLASS_SPLIT_MAX_MS,
  LOGIN_GLASS_SPLIT_ENABLED,
} from './loginGlassSplitConfig';
import {
  MENU_WIDTH_OPEN,
  WEB_MENU_SHELL_MARGIN,
  WEB_MENU_SHELL_RADIUS,
} from '@navigation/Menu/menuWebLayout';

export type GlassRect = {
  top: number;
  left: number;
  width: number;
  height: number;
  borderRadius: number;
};

export type GlassSplitTargets = {
  menu: GlassRect;
  messages: GlassRect;
  footer: GlassRect;
};

export type GlassSplitPhase = 'hidden' | 'splitting' | 'fading';

export type GlassSplitSnapshot = {
  source: GlassRect;
  targets: GlassSplitTargets;
  themeLight: boolean;
  startedAt: number;
};

type Listener = (phase: GlassSplitPhase) => void;

let phase: GlassSplitPhase = 'hidden';
let snapshot: GlassSplitSnapshot | null = null;
let dashboardReady = false;
let maxTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<Listener>();

const MESSAGES_RAIL_WIDTH = 70;
const FOOTER_SHELL_HEIGHT = 42;
const FOOTER_RIGHT_GUTTER = 35;
const MIN_VIEWPORT_FOR_SPLIT = 801;

export function isLoginGlassSplitEnabled(): boolean {
  if (!LOGIN_GLASS_SPLIT_ENABLED) return false;
  if (typeof window === 'undefined') return false;
  if (window.innerWidth < MIN_VIEWPORT_FOR_SPLIT) return false;
  try {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return false;
    }
  } catch {
    /* ignore */
  }
  return true;
}

export function getGlassSplitPhase(): GlassSplitPhase {
  return phase;
}

export function getGlassSplitSnapshot(): GlassSplitSnapshot | null {
  return snapshot;
}

export function isGlassSplitDashboardReady(): boolean {
  return dashboardReady;
}

function setPhase(next: GlassSplitPhase): void {
  if (phase === next) return;
  phase = next;
  listeners.forEach((listener) => listener(phase));
}

export function subscribeGlassSplitPhase(listener: Listener): () => void {
  listeners.add(listener);
  listener(phase);
  return () => {
    listeners.delete(listener);
  };
}

function clearMaxTimer(): void {
  if (maxTimer != null) {
    clearTimeout(maxTimer);
    maxTimer = null;
  }
}

function readGlassCardRect(): GlassRect | null {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector('.login-glassCard') as HTMLElement | null;
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 40 || r.height < 40) return null;
  const styles = window.getComputedStyle(el);
  const radius = Number.parseFloat(styles.borderRadius || '24') || 24;
  return {
    top: r.top,
    left: r.left,
    width: r.width,
    height: r.height,
    borderRadius: radius,
  };
}

function fallbackSourceRect(vw: number, vh: number): GlassRect {
  const width = Math.min(1200, Math.max(320, vw - 48));
  const height = Math.min(vh * 0.8, vh - 80);
  return {
    top: Math.max(24, (vh - height) / 2),
    left: Math.max(16, (vw - width) / 2),
    width,
    height,
    borderRadius: 24,
  };
}

export function computeGlassSplitTargets(vw: number, vh: number): GlassSplitTargets {
  const m = WEB_MENU_SHELL_MARGIN;
  const menuOuter = MENU_WIDTH_OPEN;
  const messagesOuter = MESSAGES_RAIL_WIDTH;

  const menu: GlassRect = {
    top: m,
    left: m,
    width: menuOuter - m * 2,
    height: vh - m * 2,
    borderRadius: WEB_MENU_SHELL_RADIUS,
  };

  const messages: GlassRect = {
    top: m,
    left: vw - messagesOuter + m,
    width: messagesOuter - m * 2,
    height: vh - m * 2,
    borderRadius: WEB_MENU_SHELL_RADIUS,
  };

  const footerLeft = menuOuter + vw * 0.05;
  const footerRight = FOOTER_RIGHT_GUTTER + vw * 0.05;
  const footer: GlassRect = {
    top: vh - FOOTER_SHELL_HEIGHT,
    left: footerLeft,
    width: Math.max(120, vw - footerLeft - footerRight),
    height: FOOTER_SHELL_HEIGHT,
    borderRadius: WEB_MENU_SHELL_RADIUS,
  };

  return { menu, messages, footer };
}

/**
 * Capture login glass + compute chrome targets. Call immediately before Auth reset.
 * No-op when the feature is disabled.
 */
export function beginLoginGlassSplit(themeLight: boolean): void {
  if (!isLoginGlassSplitEnabled()) return;

  clearMaxTimer();
  dashboardReady = false;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const source = readGlassCardRect() ?? fallbackSourceRect(vw, vh);

  snapshot = {
    source,
    targets: computeGlassSplitTargets(vw, vh),
    themeLight,
    startedAt: Date.now(),
  };
  setPhase('splitting');

  maxTimer = setTimeout(() => {
    requestGlassSplitFade('max-timeout');
  }, GLASS_SPLIT_MAX_MS);
}

/**
 * Called when Dashboard shell has mounted (post lazy load).
 * Safe to call when split is inactive — no-op.
 */
export function notifyDashboardShellReady(): void {
  if (phase !== 'splitting' && phase !== 'fading') return;
  dashboardReady = true;
  requestGlassSplitFade('dashboard-ready');
}

export function requestGlassSplitFade(_reason?: string): void {
  if (phase !== 'splitting') return;
  setPhase('fading');
}

export function hideLoginGlassSplit(): void {
  clearMaxTimer();
  dashboardReady = false;
  snapshot = null;
  setPhase('hidden');
  if (typeof document !== 'undefined') {
    document.documentElement.classList.remove('login-glass-splitting');
  }
}
